/* ==========================================================================
   Patient Selector Component
   Searchable dropdown to select a previous patient for auto-populate
   ========================================================================== */

import { useState, useEffect, useRef, useCallback } from 'react'
import { usePortalSlug } from '@/context/PortalContext'
import { getProviderPatients } from '@/api/portal'
import { Search, UserCheck, UserPlus, Loader2 } from 'lucide-react'
import type { PatientSummary } from '@/api/types'

/* --------------------------------------------------------------------------
   Module-level cache
   -------------------------------------------------------------------------- */

let cachedPatients: PatientSummary[] | null = null
let cachedSlug: string | null = null

interface PatientSelectorProps {
  onPatientSelect: (patient: PatientSummary | null) => void
  disabled?: boolean
  patientType?: 'human' | 'animal'
}

export function PatientSelector({
  onPatientSelect,
  disabled = false,
  patientType = 'human',
}: PatientSelectorProps) {
  const slug = usePortalSlug()
  const [query, setQuery] = useState('')
  const [patients, setPatients] = useState<PatientSummary[]>([])
  const [filtered, setFiltered] = useState<PatientSummary[]>([])
  const [isOpen, setIsOpen] = useState(false)
  const [isLoading, setIsLoading] = useState(false)
  const [selected, setSelected] = useState<PatientSummary | null>(null)
  const wrapperRef = useRef<HTMLDivElement>(null)
  const debounceRef = useRef<ReturnType<typeof setTimeout>>(undefined)

  // Close dropdown on click outside
  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target as Node)) {
        setIsOpen(false)
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  // Load patients on first focus
  const loadPatients = useCallback(async () => {
    if (cachedPatients && cachedSlug === slug) {
      setPatients(cachedPatients)
      setIsOpen(true)
      return
    }

    setIsLoading(true)
    try {
      const response = await getProviderPatients(slug)
      cachedPatients = response.patients
      cachedSlug = slug
      setPatients(response.patients)
      setIsOpen(true)
    } catch {
      // Silently fail — user can still enter manually
    } finally {
      setIsLoading(false)
    }
  }, [slug])

  const handleFocus = useCallback(async () => {
    if (patients.length === 0 && !isLoading) {
      await loadPatients()
    } else if (patients.length > 0) {
      setIsOpen(true)
    }
  }, [patients.length, isLoading, loadPatients])

  // Filter by patient type and search query
  useEffect(() => {
    let list = patients.filter((p) => p.patient_type === patientType)

    if (query.trim()) {
      const search = query.toLowerCase()
      list = list.filter((p) => {
        if (p.patient_type === 'human') {
          return (
            (p.patient_first_name || '').toLowerCase().includes(search) ||
            (p.patient_last_name || '').toLowerCase().includes(search)
          )
        }
        return (
          (p.animal_name || '').toLowerCase().includes(search) ||
          (p.owner_name || '').toLowerCase().includes(search)
        )
      })
    }

    setFiltered(list)
  }, [patients, patientType, query])

  const handleInputChange = useCallback((value: string) => {
    setQuery(value)
    if (debounceRef.current) clearTimeout(debounceRef.current)
    debounceRef.current = setTimeout(() => {
      setIsOpen(true)
    }, 150)
  }, [])

  const handleSelect = useCallback(
    (patient: PatientSummary) => {
      setSelected(patient)
      setIsOpen(false)
      setQuery('')
      onPatientSelect(patient)
    },
    [onPatientSelect]
  )

  const handleClear = useCallback(() => {
    setSelected(null)
    setQuery('')
    cachedPatients = null
    cachedSlug = null
    onPatientSelect(null)
  }, [onPatientSelect])

  function formatDate(isoString: string): string {
    const d = new Date(isoString)
    return d.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    })
  }

  // If a patient is selected, show the summary bar
  if (selected) {
    return (
      <div className="flex items-center justify-between gap-3 p-3 bg-[var(--color-soft-lavender)] border border-[var(--pharmacy-primary)] rounded-lg">
        <div className="flex items-center gap-2 min-w-0">
          <UserCheck className="h-4 w-4 flex-shrink-0 text-[var(--pharmacy-primary)]" />
          <div className="min-w-0">
            <p className="text-sm font-medium text-[var(--color-charcoal)] truncate">
              {selected.display_name}
            </p>
            <p className="text-xs text-[var(--color-gray-500)]">
              Last prescribed: {formatDate(selected.last_prescribed_at)}
            </p>
          </div>
        </div>
        <button
          type="button"
          onClick={handleClear}
          disabled={disabled}
          className="text-xs font-medium text-[var(--pharmacy-primary)] hover:underline flex-shrink-0 disabled:opacity-50"
        >
          Change
        </button>
      </div>
    )
  }

  return (
    <div ref={wrapperRef} className="relative">
      <label className="block text-sm font-medium text-[var(--color-charcoal)] mb-1.5">
        Select a previous patient or enter new
      </label>
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-[var(--color-gray-400)]" />
        <input
          type="text"
          placeholder={
            patientType === 'human'
              ? 'Search previous patients by name...'
              : 'Search previous patients by pet or owner name...'
          }
          value={query}
          onChange={(e) => handleInputChange(e.target.value)}
          onFocus={handleFocus}
          onClick={() => {
            if (patients.length > 0) setIsOpen(true)
          }}
          disabled={disabled}
          autoComplete="off"
          className="w-full pl-9 pr-3 py-2.5 text-sm border border-[var(--color-gray-300)] rounded-md bg-white text-[var(--color-charcoal)] placeholder:text-[var(--color-gray-500)] focus:outline-none focus:ring-2 focus:ring-[var(--pharmacy-primary)] focus:border-[var(--pharmacy-primary)] transition-[border-color,box-shadow] duration-150"
        />
      </div>

      {isLoading && (
        <div className="absolute z-10 w-full mt-1 bg-white border border-gray-200 rounded-md shadow-lg p-3 text-sm text-gray-500 flex items-center gap-2">
          <Loader2 className="h-4 w-4 animate-spin" />
          Loading patients...
        </div>
      )}

      {isOpen && !isLoading && (
        <ul className="absolute z-10 w-full mt-1 bg-white border border-gray-200 rounded-md shadow-lg max-h-60 overflow-auto">
          {/* New patient option */}
          <li
            onClick={() => {
              setIsOpen(false)
              setQuery('')
            }}
            className="px-3 py-2.5 hover:bg-gray-50 cursor-pointer text-sm border-b border-gray-100 flex items-center gap-2 text-[var(--color-gray-600)]"
          >
            <UserPlus className="h-4 w-4" />
            <span>Enter new patient manually</span>
          </li>

          {filtered.length === 0 && (
            <li className="px-3 py-3 text-sm text-[var(--color-gray-500)] text-center">
              No previous patients found
            </li>
          )}

          {filtered.map((patient, idx) => (
            <li
              key={`${patient.patient_type}-${patient.patient_first_name}-${patient.patient_last_name}-${patient.patient_dob}-${idx}`}
              onClick={() => handleSelect(patient)}
              className="px-3 py-2.5 hover:bg-[var(--color-soft-lavender)] cursor-pointer text-sm border-b border-gray-100 last:border-0"
            >
              <div className="font-medium text-[var(--color-charcoal)]">
                {patient.patient_type === 'human'
                  ? `${patient.patient_first_name || ''} ${patient.patient_last_name || ''}`.trim()
                  : `${patient.animal_name || ''} (${patient.animal_species || ''})`}
              </div>
              <div className="text-xs text-[var(--color-gray-500)] mt-0.5">
                {patient.patient_type === 'human'
                  ? patient.patient_dob
                    ? `DOB: ${patient.patient_dob}`
                    : ''
                  : patient.owner_name
                    ? `Owner: ${patient.owner_name}`
                    : ''}
                {' · '}
                Last Rx: {formatDate(patient.last_prescribed_at)}
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  )
}

export default PatientSelector
